import { getErrorMessage } from '../errorHandling';
import { isUploadProviderEnabled } from '../providerHelpers';
import { ArDriveProvider } from './ArDriveProvider';
import { R2Provider } from './R2Provider';
import { S3Provider } from './S3Provider';
import type { ProviderConfig, UploadProvider, WalletAdapter } from './types';

type ProviderName = UploadProvider['name'];
type ProviderKey = string;

export class UploadProviderFactory {
  private static providers = new Map<ProviderKey, UploadProvider>();

  private static getWalletIdentifier(walletAdapter: WalletAdapter): string {
    return walletAdapter.publicKey.toBase58?.() ?? 'adapter';
  }

  private static buildProviderKey(
    providerName: ProviderName,
    walletAdapter?: WalletAdapter | null,
  ): ProviderKey {
    if (!walletAdapter) {
      return `${providerName}-key`;
    }

    const walletIdentifier =
      UploadProviderFactory.getWalletIdentifier(walletAdapter);

    return `${providerName}-wallet-${walletIdentifier}`;
  }

  static async getProvider(
    providerName: ProviderName,
    config?: ProviderConfig,
  ): Promise<UploadProvider> {
    if (!isUploadProviderEnabled(providerName)) {
      throw new Error(
        `${providerName} uploads are not enabled in this environment`,
      );
    }

    const key = UploadProviderFactory.buildProviderKey(
      providerName,
      config?.walletAdapter,
    );

    const existingProvider = UploadProviderFactory.providers.get(key);
    if (existingProvider) {
      return existingProvider;
    }

    let provider: UploadProvider;

    if (providerName === 'ardrive') {
      provider = new ArDriveProvider();
    } else if (providerName === 's3') {
      provider = new S3Provider();
    } else if (providerName === 'r2') {
      provider = new R2Provider();
    } else {
      return assertUnreachable(providerName);
    }

    try {
      await provider.initialize(config);
      UploadProviderFactory.providers.set(key, provider);
    } catch (error) {
      throw new Error(
        `Failed to initialize ${providerName} provider: ${getErrorMessage(error)}`,
      );
    }

    return provider;
  }
}

const assertUnreachable = (value: never): never => {
  throw new Error(`Unknown upload provider: ${String(value)}`);
};
